import { CLOSE, CUBIC, LINE, MOVE, QUAD, type PathCommands, type TextStyle } from '@flobi/artwork-core';
import type { FaceFor, GlyphFace } from '../editor/textOutlines';
import { faceFiles } from './faceFiles';

/** The few parts of fontkit read here; it ships no types. */
interface FontkitFont {
  unitsPerEm: number;
  numGlyphs: number;
  underlinePosition: number;
  underlineThickness: number;
  italicAngle: number;
  variationAxes: Record<string, { min: number; max: number; default: number }>;
  'OS/2'?: { usWeightClass?: number; yStrikeoutPosition?: number; yStrikeoutSize?: number; fsSelection?: { italic?: boolean } };
  hasGlyphForCodePoint(codePoint: number): boolean;
  getGlyph(id: number): { path: { commands: { command: string; args: number[] }[] } };
  getVariation(settings: Record<string, number>): FontkitFont;
}

type Fontkit = { create(bytes: Uint8Array): FontkitFont | { fonts: FontkitFont[] } };

let fontkit: Promise<Fontkit> | null = null;

/** fontkit is only wanted when type is turned into outlines, so it loads then. */
function loadFontkit(): Promise<Fontkit> {
  fontkit ??= import('fontkit').then((module) => ((module as { default?: Fontkit }).default ?? module) as Fontkit);
  return fontkit;
}

class FontkitFace implements GlyphFace {
  private readonly outlines = new Map<number, PathCommands>();

  constructor(private readonly font: FontkitFont) {}

  get unitsPerEm() { return this.font.unitsPerEm; }
  get glyphCount() { return this.font.numGlyphs; }
  get weight() { return this.font['OS/2']?.usWeightClass ?? 400; }
  get italic() { return !!this.font['OS/2']?.fsSelection?.italic || this.font.italicAngle !== 0; }
  get underline() { return { position: this.font.underlinePosition || -this.unitsPerEm * 0.1, thickness: this.font.underlineThickness || this.unitsPerEm * 0.05 }; }
  get strike() {
    const os2 = this.font['OS/2'];
    return { position: os2?.yStrikeoutPosition || this.unitsPerEm * 0.28, thickness: os2?.yStrikeoutSize || this.unitsPerEm * 0.05 };
  }

  hasCharacter(codePoint: number): boolean {
    return this.font.hasGlyphForCodePoint(codePoint);
  }

  outline(glyph: number): PathCommands {
    let cmds = this.outlines.get(glyph);
    if (cmds) return cmds;
    cmds = [];
    for (const { command, args } of this.font.getGlyph(glyph).path.commands) {
      if (command === 'moveTo') cmds.push(MOVE, args[0]!, args[1]!);
      else if (command === 'lineTo') cmds.push(LINE, args[0]!, args[1]!);
      else if (command === 'quadraticCurveTo') cmds.push(QUAD, args[0]!, args[1]!, args[2]!, args[3]!);
      else if (command === 'bezierCurveTo') cmds.push(CUBIC, args[0]!, args[1]!, args[2]!, args[3]!, args[4]!, args[5]!);
      else if (command === 'closePath') cmds.push(CLOSE);
    }
    this.outlines.set(glyph, cmds);
    return cmds;
  }

  /** The same face with a variable font's axes set, or itself when it has none to set. */
  varied(settings: Record<string, number>): FontkitFace {
    const axes = this.font.variationAxes ?? {};
    const asked = Object.fromEntries(Object.entries(settings).filter(([axis]) => axis in axes));
    return Object.keys(asked).length ? new FontkitFace(this.font.getVariation(asked)) : this;
  }
}

const parsed = new WeakMap<ArrayBuffer, FontkitFace | null>();
const varied = new Map<string, FontkitFace>();

/**
 * The faces the canvas holds, parsed for their outlines, and the rule that picks one for a
 * character: its own family, the face nearest its weight and slant, else the fallbacks in
 * the order the shaper tries them — the first that has the character.
 */
export async function glyphFaces(): Promise<FaceFor> {
  const kit = await loadFontkit();
  const facesOf = (family: string): FontkitFace[] =>
    faceFiles(family).flatMap((bytes) => {
      if (!parsed.has(bytes)) {
        try {
          const font = kit.create(new Uint8Array(bytes));
          parsed.set(bytes, new FontkitFace('fonts' in font ? font.fonts[0]! : font));
        } catch {
          parsed.set(bytes, null);
        }
      }
      const face = parsed.get(bytes);
      return face ? [face] : [];
    });

  return (style: TextStyle, codePoint: number, glyph: number, fallbacks: readonly string[]) => {
    const weight = style.fontWeight ?? 400;
    const italic = style.fontStyle === 'italic';
    for (const family of [style.fontFamily ?? 'Inter', ...fallbacks]) {
      const faces = facesOf(family)
        .filter((face) => face.hasCharacter(codePoint) && glyph < face.glyphCount)
        .sort((a, b) => Number(a.italic !== italic) - Number(b.italic !== italic) || Math.abs(a.weight - weight) - Math.abs(b.weight - weight));
      const face = faces[0];
      if (!face) continue;
      const variations = style.variations ?? {};
      if (!Object.keys(variations).length) return face;
      const key = `${family}|${face.weight}|${face.italic}|${JSON.stringify(variations)}`;
      let found = varied.get(key);
      if (!found) varied.set(key, (found = face.varied(variations)));
      return found;
    }
    return null;
  };
}
